import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { FolderInput } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UploadDialogShell } from "@/components/upload/UploadDialogShell";
import { UploadSurface } from "@/components/upload/UploadSurface";

import type {
  ConflictPolicy,
  StagingMatch
} from "../../caseFiles/api/filesystemApi";
import { nodeTitle } from "../../caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import type { CollectedFile } from "../../caseFiles/upload/collectDroppedFiles";
import {
  commitStagedUpload,
  PartialCommitError,
  useStagedUpload,
  type StagedUpload
} from "../../caseFiles/upload/useStagedUpload";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseDataOptional } from "../data/CaseDataContext";
import { ClientDocumentsPicker } from "./ClientDocumentsPicker";
import { leftOutDescription } from "./leftOutFiles";
import { NodePreview } from "./NodePreview";
import { StagedFilePreview } from "./StagedFilePreview";
import {
  StagedFilesRail,
  batchOriginalFor,
  cardStateOf,
  displayPath,
  isIncluded
} from "./StagedFilesRail";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetName: string;
  parentId: string | null;
  initialCollected?: CollectedFile[] | null;
  /** The staging session, owned by the caller (see `useStagedUpload`). */
  staged: StagedUpload;
  /** After the files landed in the case: the created / replaced nodes. */
  onAdded: (nodes: FsNode[]) => void;
};

/**
 * Upload files into a case folder — as a confirmation step. Files are hashed
 * and uploaded to staging as soon as they are dropped, the rail shows what each
 * one collides with (every staged file is added unless removed; same-name files
 * in the folder ask Keep both / Replace / Skip), clicking a card pops up a
 * preview, and nothing reaches the tree until "Add N files to case". Cancel
 * throws the staging session away.
 */
export const UploadDialog: FC<Props> = ({
  open,
  onOpenChange,
  targetName,
  parentId,
  initialCollected,
  staged,
  onAdded
}) => {
  const { individualId, open: openArtifact } = useCaseIde();
  const [decisions, setDecisions] = useState<Record<string, ConflictPolicy>>(
    {}
  );
  const [committing, setCommitting] = useState(false);
  const [commitProgress, setCommitProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickingClientDocs, setPickingClientDocs] = useState(false);
  const lastSeeded = useRef<CollectedFile[] | null>(null);

  const { files, add, remove, retry, cancel, reset, isStaging } = staged;

  useEffect(() => {
    if (
      !open ||
      !initialCollected?.length ||
      lastSeeded.current === initialCollected
    )
      return;
    lastSeeded.current = initialCollected;
    void add(initialCollected);
  }, [open, initialCollected, add]);

  useEffect(() => {
    if (open) return;
    setDecisions({});
    setCommitting(false);
    setSelectedId(null);
    setPickingClientDocs(false);
    lastSeeded.current = null;
  }, [open]);

  const selected = useMemo(
    () => files.find((f) => f.id === selectedId) ?? null,
    [files, selectedId]
  );

  // When the previewed file matches a live case file, show them side by side.
  const caseData = useCaseDataOptional();
  const selectedMatch = useMemo(
    () =>
      selected?.check
        ? selected.check.duplicates[0] ??
          selected.check.possible_duplicates[0] ??
          null
        : null,
    [selected]
  );
  const matchNode = useMemo(
    () =>
      selectedMatch && caseData
        ? caseData.fs.nodes.find((n) => n.id === selectedMatch.node_id) ?? null
        : null,
    [selectedMatch, caseData]
  );
  // A same-bytes twin dropped earlier in this batch, when nothing is committed.
  const batchOriginal = useMemo(
    () => (selected ? batchOriginalFor(selected, files) : null),
    [selected, files]
  );
  // One comparison target; a committed case file wins over a same-batch twin.
  const compare = useMemo(() => {
    if (matchNode && selectedMatch)
      return { kind: "node" as const, node: matchNode, match: selectedMatch };
    if (batchOriginal) return { kind: "staged" as const, file: batchOriginal };
    return null;
  }, [matchNode, selectedMatch, batchOriginal]);

  // A file the user was previewing can leave (removed, or a cancelled upload).
  useEffect(() => {
    if (selectedId && !files.some((f) => f.id === selectedId))
      setSelectedId(null);
  }, [files, selectedId]);

  const removeFile = useCallback(
    (id: string) => {
      setSelectedId((current) => (current === id ? null : current));
      remove(id);
    },
    [remove]
  );

  const states = useMemo(
    () => new Map(files.map((f) => [f.id, cardStateOf(f, files)])),
    [files]
  );
  const included = useMemo(
    () => files.filter((f) => isIncluded(states.get(f.id) ?? "ready")),
    [files, states]
  );
  const conflicts = included.filter(
    (f) => states.get(f.id) === "conflict"
  ).length;
  // "Skip" removes a file from the count; it is still resolved.
  const toAdd = included.filter(
    (f) => !(states.get(f.id) === "conflict" && decisions[f.id] === "skip")
  ).length;

  const leftOut = useMemo(
    () =>
      files.filter((f) => {
        const state = states.get(f.id);
        return state === "unsupported" || state === "failed";
      }),
    [files, states]
  );

  const summary = [
    `${toAdd} ready`,
    conflicts
      ? `${conflicts} name ${conflicts === 1 ? "conflict" : "conflicts"}`
      : null,
    leftOut.length ? `${leftOut.length} can't be added` : null
  ]
    .filter(Boolean)
    .join(" · ");

  const showMatch = useCallback(
    (match: StagingMatch) =>
      openArtifact({ kind: "file", id: match.node_id, title: match.name }),
    [openArtifact]
  );

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const requestCancel = useCallback(() => {
    if (files.some((f) => f.storagePath || f.status === "staging"))
      setConfirmCancel(true);
    else {
      reset();
      close();
    }
  }, [files, reset, close]);

  const doCancel = useCallback(async () => {
    setConfirmCancel(false);
    close();
    await cancel();
  }, [cancel, close]);

  const commit = useCallback(async () => {
    setCommitting(true);
    try {
      const nodes = await commitStagedUpload(
        individualId,
        parentId,
        included,
        included
          .filter((f) => states.get(f.id) === "conflict")
          .map((f) => ({
            id: f.id,
            onConflict: decisions[f.id] ?? "keep_both"
          })),
        (done, total) => setCommitProgress({ done, total })
      );
      onAdded(nodes);
      const added = `Added ${nodes.length} ${
        nodes.length === 1 ? "file" : "files"
      } to ${targetName}`;
      const notAdded = leftOutDescription(leftOut.map((f) => f.name));
      if (notAdded) toast.warning(added, { description: notAdded });
      else toast.success(added);
      if (nodes.length === 1 && nodes[0].kind === "file") {
        openArtifact({
          kind: "file",
          id: nodes[0].id,
          title: nodeTitle(nodes[0])
        });
      }
      reset();
      close();
    } catch (error) {
      if (error instanceof PartialCommitError) {
        // Those files are in the case now; only the rest stay here to retry.
        onAdded(error.added);
        error.committedIds.forEach((id) => remove(id));
      }
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Couldn't add the files to the case."
      );
    } finally {
      setCommitting(false);
      setCommitProgress(null);
    }
  }, [
    remove,
    individualId,
    parentId,
    included,
    leftOut,
    states,
    decisions,
    onAdded,
    targetName,
    openArtifact,
    reset,
    close
  ]);

  const canAdd = toAdd > 0 && !isStaging && !committing;
  const hasFiles = files.length > 0;

  const previewBody = selected ? (
    compare ? (
      <div className="flex h-full min-h-0" data-testid="upload-preview-compare">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col border-r border-line-1">
          <div className="flex h-7 shrink-0 items-center border-b border-line-1 bg-fill-1 px-3 text-sm text-fg-3">
            This File
          </div>
          <div className="min-h-0 flex-1">
            <StagedFilePreview
              file={selected.file}
              name={selected.name}
              id={selected.id}
            />
          </div>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {compare.kind === "node" ? (
            <>
              <div className="flex h-7 shrink-0 items-center border-b border-line-1 bg-fill-1 px-3 text-sm text-fg-3 gap-2">
                <span
                  className="min-w-0 flex-1 truncate"
                  title={displayPath(compare.match.path)}
                >
                  {compare.match.match === "exact"
                    ? "Already in the Case"
                    : "Possible Match"}
                  {" · "}
                  {displayPath(compare.match.path)}
                </span>
                <Button
                  variant="link"
                  size="xs"
                  className="shrink-0"
                  onClick={() => showMatch(compare.match)}
                >
                  Open
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <NodePreview node={compare.node} />
              </div>
            </>
          ) : (
            <>
              <div
                className="flex h-7 shrink-0 items-center border-b border-line-1 bg-fill-1 px-3 text-sm text-fg-3"
                title={compare.file.relativePath}
              >
                <span className="min-w-0 flex-1 truncate">
                  Also in This Upload{" · "}
                  {displayPath(compare.file.relativePath)}
                </span>
              </div>
              <div className="min-h-0 flex-1">
                <StagedFilePreview
                  file={compare.file.file}
                  name={compare.file.name}
                  id={compare.file.id}
                />
              </div>
            </>
          )}
        </div>
      </div>
    ) : (
      <StagedFilePreview
        file={selected.file}
        name={selected.name}
        id={selected.id}
      />
    )
  ) : null;

  return (
    <UploadDialogShell
      open={open}
      onRequestClose={requestCancel}
      title={<>Upload to {targetName}</>}
      description={
        hasFiles
          ? 'Review the files below. They will be added to the case when you click "Add files to case".'
          : undefined
      }
      rail={
        hasFiles ? (
          <StagedFilesRail
            files={files}
            targetName={targetName}
            decisions={decisions}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onDecide={(id, policy) =>
              setDecisions((d) => ({ ...d, [id]: policy }))
            }
            onRemove={removeFile}
            onRetry={(id) => void retry(id)}
            onShow={showMatch}
          />
        ) : null
      }
      surface={
        <UploadSurface onFiles={(c) => void add(c)} disabled={committing} />
      }
      below={
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPickingClientDocs(true)}
            disabled={committing}
            data-testid="upload-from-client-docs"
          >
            <FolderInput />
            From Client Documents
          </Button>
        </div>
      }
      preview={
        selected
          ? {
              id: selected.id,
              name: selected.name,
              onClose: () => setSelectedId(null),
              body: previewBody
            }
          : null
      }
      summary={hasFiles ? summary : null}
      onCancel={requestCancel}
      cancelDisabled={committing}
      action={{
        onClick: () => void commit(),
        disabled: !canAdd,
        busy: committing || isStaging,
        label:
          commitProgress && commitProgress.total > 1 ? (
            <span data-testid="upload-commit-progress">
              Adding {commitProgress.done} of {commitProgress.total}…
            </span>
          ) : committing ? (
            "Adding…"
          ) : (
            <>
              Add {toAdd} {toAdd === 1 ? "file" : "files"} to case
            </>
          )
      }}
    >
      <ClientDocumentsPicker
        open={pickingClientDocs}
        onOpenChange={setPickingClientDocs}
        individualId={individualId}
        parentId={parentId}
        targetName={targetName}
        onAdded={(nodes, { complete }) => {
          onAdded(nodes);
          if (complete && !hasFiles) close();
        }}
      />
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard these uploads?</AlertDialogTitle>
            <AlertDialogDescription>
              Nothing has been added to {targetName}. The files you uploaded
              here will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep reviewing</AlertDialogCancel>
            <AlertDialogAction onClick={() => void doCancel()}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UploadDialogShell>
  );
};

/** `UploadDialog` owning its own staging session for the current case. */
export const CaseUploadDialog: FC<Omit<Props, "staged">> = ({
  parentId,
  ...props
}) => {
  const { individualId } = useCaseIde();
  const staged = useStagedUpload(individualId, parentId);
  return <UploadDialog {...props} parentId={parentId} staged={staged} />;
};
